
import { useCallback, useRef } from 'react';
import { updatePerformanceHUD } from '@/studio/PerformanceHUDManager';
import { SimulationConfig, Ball, MorphData, ShapeType, TrailCircle, CollisionEvent, BlastEvent, EscapeRing, SpaceSpiralRoundConfig, BallPiece, RhythmicSimulationState } from '@/types';
import { TrailCirclePool } from '@/services/TrailCirclePool';
import { SHAPES } from '@/constants/constants';
import { AUDIO } from '@/services/AudioService';
import { PhysicsSystem } from '@/logic/physics/PhysicsSystem';
import { BallPool } from '@/services/BallPool';
import { CollisionEventPool } from '@/services/CollisionEventPool';
import { BlastEventPool } from '@/services/BlastEventPool';
import { CoreSimulationLogic } from '@/logic/CoreSimulationLogic';
import { random } from '@/lib/random';
import { SpawnerPhysics } from '@/logic/modes/SpawnerPhysics';
import { OffscreenCanvasManager } from '@/services/OffscreenCanvasManager';
import { FrameScheduler } from '@/services/FrameScheduler';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';

export const useSimulationLoop = (
  configRef: React.MutableRefObject<SimulationConfig>,
  isPlaying: boolean,
  ballsRef: React.MutableRefObject<Ball[]>,
  trailCirclesRef: React.MutableRefObject<TrailCircle[]>,
  collisionEventsRef: React.MutableRefObject<CollisionEvent[]>,
  blastEventsRef: React.MutableRefObject<BlastEvent[]>,
  escapeRingsRef: React.MutableRefObject<EscapeRing[]>,
  lastTimeRef: React.MutableRefObject<number>,
  hueRef: React.MutableRefObject<number>,
  timeScaleRef: React.MutableRefObject<number>,
  isRotationStoppedRef: React.MutableRefObject<boolean>,
  shakeRef: React.MutableRefObject<number>,
  counterPopRef: React.MutableRefObject<number>,
  timeRemainingRef: React.MutableRefObject<number>,
  morphTimerRef: React.MutableRefObject<number>,
  morphStateRef: React.MutableRefObject<{ active: boolean, from: ShapeType, to: ShapeType, progress: number }>,
  escapeAccelerationBoostRef: React.MutableRefObject<number>,
  containerRadiusRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRef: React.MutableRefObject<number>,
  rhythmicTimeScaleRateRef: React.MutableRefObject<number>,
  currentRoundRef: React.MutableRefObject<number>,
  roundStartTimeRef: React.MutableRefObject<number>,
  roundOverridesRef: React.MutableRefObject<SpaceSpiralRoundConfig | null>,
  physicsSystemRef: React.MutableRefObject<PhysicsSystem>,
  workerRef: React.MutableRefObject<Worker | null>,
  ballPoolRef: React.MutableRefObject<BallPool>,
  trailCirclePoolRef: React.MutableRefObject<TrailCirclePool>,
  collisionEventPoolRef: React.MutableRefObject<CollisionEventPool>,
  blastEventPoolRef: React.MutableRefObject<BlastEventPool>,
  speedIncreaseRateRef: React.MutableRefObject<number>,
  ballGrowthRateRef: React.MutableRefObject<number>,
  containerShrinkRateRef: React.MutableRefObject<number>,
  rhythmicBreakPiecesRef: React.MutableRefObject<BallPiece[]>,
  persistentSpectrumBoostRef: React.MutableRefObject<Float32Array>,
  persistentSpectrumTargetRef: React.MutableRefObject<Float32Array>,
  rhythmicResetTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>,
  rhythmicIndexRef: React.MutableRefObject<number>,
  rhythmicSimulationsStateRef: React.MutableRefObject<RhythmicSimulationState[]>,
  hyperSpeedStateRef: React.MutableRefObject<{ active: boolean, startTime: number }>,
  zoomLevelRef: React.MutableRefObject<number>,
  requestRef: React.MutableRefObject<number>,
  simTimeRef: React.MutableRefObject<number>,
  setElapsedTime: React.Dispatch<React.SetStateAction<number>>,
  setBounceCount: React.Dispatch<React.SetStateAction<number>>,
  setBallCount: React.Dispatch<React.SetStateAction<number>>,
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>,
  setIsPlaying: React.Dispatch<React.SetStateAction<boolean>>,
  triggerBlast: (b: {pos: {x: number, y: number}, color: string, radius: number}, cmr?: number, gapAngle?: number, morph?: MorphData, animType?: string) => void,
  playCollisionSound: (isDestruction?: boolean) => void,
  initSimulation: (resetRounds?: boolean) => void,
  draw: () => void,
  rhythmicEscapeShieldActiveRef: React.MutableRefObject<boolean>,
  rhythmicEscapeShieldBouncesRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldTimeRef: React.MutableRefObject<number>,
  rhythmicEscapeShieldAngleRef: React.MutableRefObject<number>,
  bounceCountRef?: React.MutableRefObject<number>,
  ballCountRef?: React.MutableRefObject<number>,
  elapsedTimeRef?: React.MutableRefObject<number>
) => {
  const lastFpsUpdateRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);
  const currentFpsRef = useRef<number>(0);
  const update = useCallback((manualDt?: number) => {
    const cfg = configRef.current; 
    
    const isWorker = typeof window !== 'undefined' && window.location.search.includes('isWorker=true');
    
    // Only schedule next frame if NOT in cloud rendering worker mode
    if (!isWorker) {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        requestRef.current = requestAnimationFrame(() => update());
    }

    if (!isPlaying && !isWorker) {
        draw(); lastTimeRef.current = Date.now(); return;
    }
    
    const startFrameTime = performance.now();
    let now: number, deltaTime: number;
    if (isWorker && manualDt !== undefined) {
        deltaTime = manualDt;
        simTimeRef.current += deltaTime;
        now = simTimeRef.current;
    } else {
        const absoluteNow = Date.now();
        
        // Phase 4: Local Performance Optimization - FPS Capping
        const targetSimulationFps = cfg.simulationFPS || 0;
        if (targetSimulationFps > 0) {
            const frameInterval = 1000 / targetSimulationFps;
            if (absoluteNow - lastTimeRef.current < frameInterval - 1) {
                // If we're reaching this too often, requestAnimationFrame might be high frequency (e.g. 144Hz)
                // Skip the simulation update to match desired target FPS
                return;
            }
        }

        deltaTime = Math.max(1, absoluteNow - lastTimeRef.current);
        
        // Safety: Prevent huge jumps which cause physics chaos
        if (deltaTime > 100) deltaTime = 16.67;
        
        simTimeRef.current += deltaTime;
        now = simTimeRef.current; // Use simTime instead of absoluteNow for deterministic logic
        lastTimeRef.current = absoluteNow; 
    }

    try {
        // Handle initial growth/speed sync (if not manual)
        if (ballGrowthRateRef.current !== cfg.ballGrowthRate && !cfg.ballGrowthAcceleration) ballGrowthRateRef.current = cfg.ballGrowthRate;
        if (speedIncreaseRateRef.current !== cfg.speedIncreaseRate && !cfg.speedAcceleration) speedIncreaseRateRef.current = cfg.speedIncreaseRate;
        if (containerShrinkRateRef.current !== cfg.containerShrinkRate && !cfg.containerShrinkAcceleration) containerShrinkRateRef.current = cfg.containerShrinkRate;

        // Delegate to Core Logic
        const isTurbo = cfg.enableHyperSpeed && cfg.mode === 'ESCAPE';
        const batchSize = isTurbo ? (cfg.hyperSpeedBatchSize || 1) : 1;
        const targetDt = isTurbo ? (cfg.hyperSpeedTargetTime || 16.67) : deltaTime;

        let result: any = null;
        for (let b = 0; b < batchSize; b++) {
            result = CoreSimulationLogic.update(
                targetDt,
                cfg,
                {
                    balls: ballsRef.current,
                    trailCircles: trailCirclesRef.current,
                    collisionEvents: collisionEventsRef.current,
                    blastEvents: blastEventsRef.current,
                    escapeRings: escapeRingsRef.current,
                    hue: hueRef.current,
                    timeScale: timeScaleRef.current,
                    isRotationStopped: isRotationStoppedRef.current,
                    shake: shakeRef.current,
                    counterPop: counterPopRef.current,
                    timeRemaining: timeRemainingRef.current,
                    morphTimer: morphTimerRef.current,
                    morphState: morphStateRef.current,
                    escapeAccelerationBoost: escapeAccelerationBoostRef.current,
                    containerRadius: containerRadiusRef.current,
                    rhythmicTimeScale: rhythmicTimeScaleRef.current,
                    rhythmicTimeScaleRate: rhythmicTimeScaleRateRef.current,
                    rhythmicBreakPieces: rhythmicBreakPiecesRef.current,
                    persistentSpectrumBoost: persistentSpectrumBoostRef.current,
                    persistentSpectrumTarget: persistentSpectrumTargetRef.current,
                    rhythmicResetTimeout: rhythmicResetTimeoutRef.current,
                    rhythmicIndex: rhythmicIndexRef.current,
                    hyperSpeedActive: hyperSpeedStateRef.current.active,
                    zoomLevel: zoomLevelRef.current,
                    simTime: simTimeRef.current,
                },
                {
                    roundStartTime: roundStartTimeRef,
                    currentRound: currentRoundRef,
                    rhythmicSimulationsState: rhythmicSimulationsStateRef,
                    ballPool: ballPoolRef,
                    trailCirclePool: trailCirclePoolRef,
                    collisionEventPool: collisionEventPoolRef,
                    blastEventPool: blastEventPoolRef,
                    worker: workerRef,
                    rhythmicIndex: rhythmicIndexRef,
                    rhythmicTimeScale: rhythmicTimeScaleRef,
                    rhythmicTimeScaleRate: rhythmicTimeScaleRateRef,
                    rhythmicBreakPieces: rhythmicBreakPiecesRef,
                    rhythmicResetTimeout: rhythmicResetTimeoutRef,
                    physicsSystem: physicsSystemRef,
                    hyperSpeedState: hyperSpeedStateRef,
                    ballGrowthRate: ballGrowthRateRef,
                    containerShrinkRate: containerShrinkRateRef,
                    speedIncreaseRate: speedIncreaseRateRef,
                    rhythmicEscapeShieldActive: rhythmicEscapeShieldActiveRef,
                    rhythmicEscapeShieldBounces: rhythmicEscapeShieldBouncesRef,
                    rhythmicEscapeShieldTime: rhythmicEscapeShieldTimeRef,
                    rhythmicEscapeShieldAngle: rhythmicEscapeShieldAngleRef
                },
                {
                    setElapsedTime,
                    setBounceCount,
                    setBallCount,
                    setConfig,
                    setIsPlaying,
                    triggerBlast,
                    playCollisionSound,
                    initSimulation,
                    draw
                }
            );

            if (result) {
                // Sync back state intermediate if in turbo, to ensure next iteration has correct data
                ballsRef.current = result.updatedState.balls;
                hueRef.current = result.updatedState.hue;
                timeScaleRef.current = result.updatedState.timeScale;
                shakeRef.current = result.updatedState.shake;
                counterPopRef.current = result.updatedState.counterPop;
                timeRemainingRef.current = result.updatedState.timeRemaining;
                escapeAccelerationBoostRef.current = result.updatedState.escapeAccelerationBoost;
                containerRadiusRef.current = result.updatedState.containerRadius;
                simTimeRef.current = result.updatedState.simTime;
                rhythmicBreakPiecesRef.current = result.updatedState.rhythmicBreakPieces;
                zoomLevelRef.current = result.updatedState.zoomLevel;
                
                // If we reach 0 rings in escape mode, stop the batch early
                if (cfg.mode === 'ESCAPE' && escapeRingsRef.current.length === 0) break;
            }
        }

        // 5. Spawner Audio Peak Logic
        if (cfg.spawnOnAudioPeak && AUDIO) {
            const freqData = AUDIO.getFrequencyData();
            if (freqData && freqData.length > 0) {
                let total = 0;
                const count = 10;
                for (let i = 0; i < count; i++) total += freqData[i];
                const avg = total / (count * 255);
                
                const lastPeakTime = (window as any).lastSpawnerPeakTime || 0;
                const threshold = cfg.spawnAudioThreshold || 0.6;

                if (avg > threshold && now - lastPeakTime > 200) {
                    (window as any).lastSpawnerPeakTime = now;
                    if (ballsRef.current.length < cfg.maxBalls) {
                        const nb = ballPoolRef.current.get(
                            `peak-${now}`,
                            { x: 0, y: 0 },
                            SpawnerPhysics.getSpawnVelocity(cfg),
                            cfg.ballRadius,
                            cfg.useBallRGB ? `hsl(${hueRef.current}, 100%, 50%)` : cfg.ball1Color,
                            cfg.ballMass,
                            cfg.ballStyle
                        );
                        ballsRef.current.push(nb);
                        if (workerRef.current) workerRef.current.postMessage({ type: 'ADD_BALLS', payload: { balls: [nb], now } });
                        setBallCount(ballsRef.current.length);
                    }
                }
            }
        }

        const cpuEnd = performance.now();
        PerformanceTelemetry.recordCpuTime(cpuEnd - startFrameTime);

        // Reset frame counters (draw call accumulations) before rendering
        PerformanceTelemetry.resetFrameCounters();

        const renderStart = performance.now();
        draw();
        PerformanceTelemetry.recordGpuTime(performance.now() - renderStart);

        // Sync real-time background color wheel shifts off-thread
        OffscreenCanvasManager.setHue(hueRef.current);

        // Direct performance HUD updates bypassing stable React components
        frameCountRef.current++;
        const absoluteAbsoluteNow = performance.now();
        if (absoluteAbsoluteNow - lastFpsUpdateRef.current >= 1000) {
            currentFpsRef.current = Math.round((frameCountRef.current * 1000) / (absoluteAbsoluteNow - lastFpsUpdateRef.current));
            frameCountRef.current = 0;
            lastFpsUpdateRef.current = absoluteAbsoluteNow;
        }

        PerformanceTelemetry.fps = currentFpsRef.current;
        PerformanceTelemetry.updateVRAMEstimation(cfg, ballsRef.current.length, window.innerWidth, window.innerHeight);

        updatePerformanceHUD(
            elapsedTimeRef ? elapsedTimeRef.current : simTimeRef.current,
            bounceCountRef ? bounceCountRef.current : 0,
            ballCountRef ? ballCountRef.current : ballsRef.current.length,
            escapeRingsRef.current.length,
            timeRemainingRef.current,
            currentRoundRef.current,
            containerRadiusRef.current,
            cfg.rhythmicEscapeShieldRingRadius || 100,
            cfg.mode,
            currentFpsRef.current
        );

        const durationMS = performance.now() - startFrameTime;
        FrameScheduler.registerFrame(durationMS, cfg.simulationFPS || 60);
    } catch (e) {
        console.error("SIMULATION ENGINE CRASHED. Attempting recovery...", e);
        // We already scheduled the next frame at the top, so it will retry.
        // We might want to force a small cooldown or reset if it's too frequent.
    }
  }, [isPlaying, initSimulation, draw]);

  return { update };
};
